'use client';

/**
 * Nova Store Context
 * 
 * Provides a React context for sharing a single Nova store instance across components.
 */

import * as React from 'react';
import { Engine } from '@/lib/engine';
import { useNovaStore, NovaStore } from './nova-store';

// Context interface that includes both the store and the engine
interface NovaStoreContextValue {
  store: NovaStore;
  engine: Engine;
}

// Create a React context for the Nova store
const NovaStoreContext = React.createContext<NovaStoreContextValue | null>(null);

// Provider props interface
interface NovaStoreProviderProps {
  engine: Engine;
  children: React.ReactNode;
}

/**
 * Nova Store Provider Component
 * 
 * Provides the Nova store to all child components via React context.
 */
export function NovaStoreProvider({ engine, children }: NovaStoreProviderProps) {
  const store = useNovaStore({ engine });
  
  // Create the context value with both store and engine
  const contextValue = React.useMemo(() => ({
    store,
    engine
  }), [store, engine]);
  
  return (
    <NovaStoreContext.Provider value={contextValue}>
      {children}
    </NovaStoreContext.Provider>
  );
}

/**
 * Hook to access the Nova store from context
 * 
 * This should be used by all components that need access to the Nova store.
 */
export function useNovaStoreContext() {
  const context = React.useContext(NovaStoreContext);
  
  if (!context) {
    throw new Error('useNovaStoreContext must be used within a NovaStoreProvider');
  }
  
  const {
    activeTable,
    data,
    columns,
    columnRefs,
    originalColumnRefs,
    query,
    lastExecutedQuery,
    lastExecutedView,
    status,
    error,
    isQueryBuilderExpanded,
    lastExpandedSize,
    panelWidths,
    executeQuery,
    updateQuery,
    setActiveTable,
    setQueryBuilderExpanded,
    setLastExpandedSize,
    setPanelWidth,
    logState,
    runSqlQuery
  } = context.store;
  
  // Return store properties and actions
  return {
    // Data properties
    activeTable,
    data,
    columns,
    columnRefs,
    originalColumnRefs,
    
    // Query properties
    query,
    lastExecutedQuery,
    lastExecutedView,
    
    // Status properties
    status,
    error,
    
    // UI properties
    isQueryBuilderExpanded,
    lastExpandedSize,
    panelWidths,
    
    // Actions
    executeQuery,
    updateQuery,
    setActiveTable,
    setQueryBuilderExpanded,
    setLastExpandedSize,
    setPanelWidth,
    logState,
    runSqlQuery
  };
}

/**
 * Hook to access the engine from context
 * 
 * This should be used by all components that need access to the engine.
 */
export function useNovaEngine() {
  const context = React.useContext(NovaStoreContext);
  
  if (!context) {
    throw new Error('useNovaEngine must be used within a NovaStoreProvider');
  }
  
  return context.engine;
} 